/**
 * The food database a meal option's rows are built from.
 *
 * Four calls, each on its own cadence: a search as the Dish field is typed
 * into, a food's units once it is picked, the nutrient profile for a quantity
 * in one of them once both are in, and the unit list on its own, for a row
 * that has no food behind it to ask about. All four sit behind the bearer
 * token like the rest of the API, but need nothing about a workspace - the
 * catalog is the same for every dietitian.
 *
 * Beside them, the one thing about the search that is the workspace's own:
 * whether it covers the shared catalog at all, or only the dishes the
 * workspace has written for itself. See `fetchDishScope`.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { PaginatedResponse } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import type {
  CatalogPreferenceMode,
  CatalogPreferenceResponse,
  DishScope,
  FoodNutritionResponse,
  FoodNutritionResult,
  FoodOption,
  FoodSummaryResponse,
  FoodUnitCatalogResponse,
  FoodUnitOption,
  FoodUnitsResponse,
  UpdateCatalogPreferencePayload,
} from "../types/plans.types";

/** How many results a page of the search carries. */
export const FOOD_SEARCH_PAGE_SIZE = 20;

/**
 * What the backend calls a dish belonging to the workspace that asked, as
 * against the catalog every dietitian shares. The only place the word is read.
 */
const WORKSPACE_SCOPE = "WORKSPACE";

/**
 * The switch's two settings, under the backend's names for them. The only
 * place those names are written.
 */
const PREFERENCE_MODES: Record<DishScope, CatalogPreferenceMode> = {
  mine: "OWN_ONLY",
  all: "OWN_AND_GLOBAL",
};

/**
 * `mode` off the wire, settled into the switch's own vocabulary. Anything
 * that is not the narrowed setting reads as the whole catalog — the way a
 * search that had never been narrowed would behave — so a word the switch
 * has never heard of cannot make it claim a narrowing it does not know about.
 */
const toDishScope = (response: CatalogPreferenceResponse | undefined): DishScope =>
  response?.mode === PREFERENCE_MODES.mine ? "mine" : "all";

async function requireToken(getToken: GetToken, action: string): Promise<string> {
  const token = await getToken();
  if (!token) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${action}.`);
  }
  return token;
}

export interface FoodSearchPage {
  results: FoodOption[];
  hasNext: boolean;
}

export interface SearchFoodsOptions {
  /** Matched case-insensitively, substring or otherwise, by the backend. Blank asks for the unfiltered catalog. */
  query: string;
  /** 1-based, the way the endpoint counts. */
  page?: number;
  pageSize?: number;
  signal?: AbortSignal;
}

/** One page of the Dish field's search, `GET /api/v1/foods/?q=&page=&page_size=`. */
export async function searchFoods(
  getToken: GetToken,
  { query, page, pageSize = FOOD_SEARCH_PAGE_SIZE, signal }: SearchFoodsOptions
): Promise<FoodSearchPage> {
  const token = await requireToken(getToken, "search foods");

  const response = await api.get<PaginatedResponse<FoodSummaryResponse>>(
    API_ENDPOINTS.foods.list,
    {
      token,
      query: { q: query.trim() || undefined, page, page_size: pageSize },
      signal,
    }
  );

  return {
    results: (response?.results ?? []).map((row) => ({
      id: row.id,
      name: row.name,
      // A row matched on its own name carries it twice; one sent without a
      // `matched_name` at all falls back to the name, so the field always has
      // something to show.
      matchedName: row.matched_name || row.name,
      dietaryPattern: row.dietary_pattern,
      // The scope settled here rather than passed up: a row sent without one
      // is not the workspace's, which is the safe way round — a global dish
      // wrongly marked as yours is a claim about your data, while an unmarked
      // dish of yours is only a missing mark.
      isOwn: row.scope === WORKSPACE_SCOPE,
    })),
    hasNext: !!response?.next,
  };
}

/** Which dishes the search covers for this workspace, `GET /api/v1/catalog-preference/`. */
export async function fetchDishScope(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<DishScope> {
  const token = await requireToken(getToken, "read the dish search setting");

  const response = await api.get<CatalogPreferenceResponse>(
    API_ENDPOINTS.foods.catalogPreference,
    { token, signal }
  );
  return toDishScope(response);
}

/**
 * Changes it, `PUT /api/v1/catalog-preference/`, and answers with what was
 * stored — which is what the switch settles on, rather than what was asked.
 */
export async function saveDishScope(
  getToken: GetToken,
  scope: DishScope,
  signal?: AbortSignal
): Promise<DishScope> {
  const token = await requireToken(getToken, "change the dish search setting");

  const payload: UpdateCatalogPreferencePayload = { mode: PREFERENCE_MODES[scope] };
  const response = await api.put<CatalogPreferenceResponse>(
    API_ENDPOINTS.foods.catalogPreference,
    payload,
    { token, signal }
  );
  return toDishScope(response);
}

/** What one food can be measured in, `GET /api/v1/foods/{id}/units/`. */
export async function fetchFoodUnits(
  getToken: GetToken,
  foodId: number,
  signal?: AbortSignal
): Promise<FoodUnitOption[]> {
  const token = await requireToken(getToken, "load a food's units");

  const response = await api.get<FoodUnitsResponse>(
    API_ENDPOINTS.foods.units(foodId),
    { token, signal }
  );

  return (response?.units ?? []).map((unit) => ({
    id: unit.id,
    name: unit.name,
    // `?? null` rather than passed through: a unit sent without a conversion
    // is one the field shows by name alone, and `undefined` and `null` should
    // not be two ways of saying that.
    baseAmount: unit.base_amount ?? null,
    baseUnit: unit.base_unit ?? null,
  }));
}

/**
 * Every unit the catalog knows, `GET /api/v1/food-units/`.
 *
 * The list a dish written out by hand is measured from: there is no food
 * behind such a row to ask `fetchFoodUnits` about, but "Katori" means the same
 * thing whether or not the catalog has heard of what is in it.
 *
 * Read back as `FoodUnitOption`s with no conversion on them, which is what
 * they are — the endpoint sends a name and an id, and what one of them weighs
 * is the dietitian's to say on the row itself.
 */
export async function fetchFoodUnitCatalog(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<FoodUnitOption[]> {
  const token = await requireToken(getToken, "load the unit list");

  const response = await api.get<FoodUnitCatalogResponse>(
    API_ENDPOINTS.foods.unitCatalog,
    { token, signal }
  );

  return (response?.units ?? []).map((unit) => ({
    id: unit.id,
    name: unit.name,
    baseAmount: null,
    baseUnit: null,
  }));
}

export interface FetchFoodNutritionOptions {
  foodId: number;
  unitId: number;
  quantity: number;
  signal?: AbortSignal;
}

/**
 * The nutrient profile of `quantity` of `foodId` in `unitId`.
 *
 * Calories and fat are read off the response's `total_` figures, which fold
 * in the cooking fat a homemade dish was made with; protein off the adjusted
 * figure, which is what the body can absorb rather than the food's gross
 * content - see `FoodNutritionResult`.
 */
export async function fetchFoodNutrition(
  getToken: GetToken,
  { foodId, unitId, quantity, signal }: FetchFoodNutritionOptions
): Promise<FoodNutritionResult> {
  const token = await requireToken(getToken, "load a food's nutrition");

  const response = await api.get<FoodNutritionResponse>(
    API_ENDPOINTS.foods.nutrition(foodId),
    { token, query: { unit_id: unitId, quantity }, signal }
  );

  return {
    macros: {
      calories: Math.round(response.nutrition.total_kcal),
      carbs: Math.round(response.nutrition.carbs_g),
      protein: Math.round(response.nutrition.protein_adjusted_g),
      fat: Math.round(response.nutrition.total_fat_g),
    },
    unitLabel: response.unit_label,
  };
}
